// Position display helpers shared by the board cells, the pick flash and the
// roster panel. The players table stores Sleeper's "DEF"; roster_config and
// everything user-facing says "DST", so every label goes through here.

/** Slot/label name for a raw player position. */
export function displayPosition(position: string | null | undefined): string {
  if (!position) return '--'
  const upper = position.toUpperCase()
  return upper === 'DEF' ? 'DST' : upper
}

// Written out as whole class names, not built by concatenation: Tailwind
// scans source text for candidates and never sees `bg-pos-${x}`.
const POSITION_FILL: Record<string, string> = {
  QB: 'bg-pos-qb',
  RB: 'bg-pos-rb',
  WR: 'bg-pos-wr',
  TE: 'bg-pos-te',
  K: 'bg-pos-k',
  DST: 'bg-pos-dst',
}

/** Solid background class for a position; grey for anything unrecognised. */
export function positionFill(position: string | null | undefined): string {
  return POSITION_FILL[displayPosition(position)] ?? 'bg-ink-lo'
}

// The same six colours as a border. The pick flash's flare ring is drawn by the
// overlay at the landing cell's viewport coordinates, so it is a sibling of the
// card rather than a child and cannot inherit its text-pos-ink. Giving it the
// colour as its own class beats setting a custom property on the wrapper and
// reading it back through border-current: one mechanism instead of two, and it
// stays consistent with how every other position colour on the board is applied.
const POSITION_BORDER: Record<string, string> = {
  QB: 'border-pos-qb',
  RB: 'border-pos-rb',
  WR: 'border-pos-wr',
  TE: 'border-pos-te',
  K: 'border-pos-k',
  DST: 'border-pos-dst',
}

/** Border colour class for a position; grey for anything unrecognised. */
export function positionBorder(position: string | null | undefined): string {
  return POSITION_BORDER[displayPosition(position)] ?? 'border-ink-lo'
}

const NAME_SUFFIXES = new Set(['JR', 'JR.', 'SR', 'SR.', 'II', 'III', 'IV', 'V'])

/**
 * "Ja'Marr Chase" -> "J. Chase".
 *
 * A 14-team board at 4K gives each cell ~267x113 CSS px for two lines, so the
 * first name is always an initial and trailing generational suffixes are
 * dropped — they cost as much width as a surname and carry none of the
 * information. Team defenses collapse to their nickname instead: "B. Ravens"
 * would be nonsense.
 */
export function abbreviateName(
  fullName: string,
  position?: string | null,
): string {
  const name = (fullName ?? '').trim()
  if (!name) return ''

  const parts = name.split(/\s+/)
  if (displayPosition(position) === 'DST') return parts[parts.length - 1]

  const words = parts.filter((part) => !NAME_SUFFIXES.has(part.toUpperCase()))
  if (words.length < 2) return words[0] ?? name

  return `${words[0][0]}. ${words[words.length - 1]}`
}

/**
 * "Ja'Marr Chase" -> "JC". The monogram a Portrait shows until a real headshot
 * exists.
 *
 * Defenses get their NFL abbreviation instead: Sleeper stores them with a
 * team-abbreviation id and no personal name, so "SR" for "Seattle Seahawks"
 * would be meaningless where "SEA" is exactly what you'd expect. Suffixes are
 * dropped for the same reason abbreviateName drops them — "MJ" is right for
 * "Marvin Harrison Jr.", "MH" would be, "MJ" from Harrison/Jr. would not.
 */
export function playerInitials(
  fullName: string,
  position?: string | null,
  nflTeam?: string | null,
): string {
  const name = (fullName ?? '').trim()

  if (displayPosition(position) === 'DST') {
    if (nflTeam) return nflTeam.toUpperCase().slice(0, 3)
    const last = name.split(/\s+/).filter(Boolean).pop()
    return (last ?? '').slice(0, 3).toUpperCase()
  }

  const words = name.split(/\s+/).filter((part) => part && !NAME_SUFFIXES.has(part.toUpperCase()))
  if (words.length === 0) return '--'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
}
